import React, { useEffect, useState } from 'react';
import './step-detail.css';
import { getSupabase } from '../lib/supabase.js';
import { labelLoopSteps } from '../lib/useInvestmentData.js';

const MEMBER_PALETTE = ['#4f46e5', '#0ea5e9', '#f59e0b', '#ec4899', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6'];
function memberColor(id) {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return MEMBER_PALETTE[h % MEMBER_PALETTE.length];
}
const initial = (name) => (name || '?').trim().charAt(0).toUpperCase() || '?';

// ─── Helpers ───
const fmt$ = (n, d = 0) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const v = Math.abs(n);
  return (n < 0 ? '-' : '') + '$' + v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmt$Big = (n) => {
  if (n >= 1000) return '$' + (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'K';
  return '$' + n.toLocaleString();
};
const fmtPct = (n, d = 1) => (n * 100).toFixed(d).replace(/\.0$/, '') + '%';
const fmtDate = (s) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return String(s).slice(0, 10); }
};
const fmtTime = (s) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch { return ''; }
};
const kindLabel = (k) => (k || '').replace(/_/g, ' ');

// Theme color for loops (matches useCatalogData).
const THEME_HEX = {
  coral: '#f97316', teal: '#0d9488', purple: '#7c3aed', amber: '#d97706',
  green: '#10b981', blue: '#2563eb', rose: '#e11d48', indigo: '#4f46e5',
};
const GLYPHS = ['⚡', '◐', '◇', '◆', '◈', '◓', '◑', '⬢'];
function themeGlyph(loopId) {
  let h = 0;
  for (let i = 0; i < (loopId || '').length; i++) h = (h * 31 + loopId.charCodeAt(i)) >>> 0;
  return GLYPHS[h % GLYPHS.length];
}

function shade(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt, G = (num >> 8 & 0xFF) + amt, B = (num & 0xFF) + amt;
  const clamp = v => Math.max(0, Math.min(255, v));
  return '#' + (0x1000000 + clamp(R) * 0x10000 + clamp(G) * 0x100 + clamp(B)).toString(16).slice(1);
}

// ─── Data hook ───
// Loads: step row, value_events for this step + this enrolled member's family,
// family members (account holder + sub-accounts), financial accounts (for
// institution names), and loops containing this step.
function useStepDetailData(stepId, member) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      if (!sb) { setState({ status: 'error', data: null, error: new Error('No Supabase client') }); return; }
      if (!stepId) { setState({ status: 'no-step', data: null, error: null }); return; }
      if (!member?.id) { setState({ status: 'no-member', data: null, error: null }); return; }
      try {
        const [stepRes, eventsRes, subsRes, acctsRes, refsRes] = await Promise.all([
          sb.from('steps').select('*').eq('id', stepId).maybeSingle(),
          sb.from('value_events').select('*').eq('step_id', stepId).eq('enrolled_member_id', member.id).is('voided_at', null).order('event_date', { ascending: true }),
          sb.from('members').select('id, name, display_name').eq('managed_by_member_id', member.id).eq('active', true),
          sb.from('financial_accounts').select('id, member_id, account_kind, institution, active').eq('active', true),
          sb.from('loop_step_refs').select('loop_id, step_order').eq('step_id', stepId),
        ]);

        if (cancelled) return;
        const step = stepRes.data;
        if (!step) { setState({ status: 'not-found', data: null, error: null }); return; }

        const subs = subsRes.data || [];
        const familyIds = [member.id, ...subs.map((s) => s.id)];

        // Pull each loop the step appears in.
        const loopIds = [...new Set((refsRes.data || []).map((r) => r.loop_id))];
        const [loopsRes, allRefsRes, mySubsRes] = await Promise.all([
          loopIds.length ? sb.from('loops').select('*').in('id', loopIds) : Promise.resolve({ data: [] }),
          loopIds.length ? sb.from('loop_step_refs').select('loop_id, step_id, step_order, steps(source_kind, dest_kind, value_rate)').in('loop_id', loopIds) : Promise.resolve({ data: [] }),
          loopIds.length ? sb.from('loop_subscriptions').select('loop_id').eq('enrolled_member_id', member.id).eq('active', true).in('loop_id', loopIds) : Promise.resolve({ data: [] }),
        ]);
        if (cancelled) return;

        // Family in render shape.
        const holderName = member.display_name || member.name || 'You';
        const FAMILY = [
          { id: member.id, name: holderName, initials: initial(holderName), role: 'holder', color: memberColor(member.id) },
          ...subs.map((s) => {
            const nm = s.display_name || s.name || s.id;
            return { id: s.id, name: nm, initials: initial(nm), role: 'sub', color: memberColor(s.id) };
          }),
        ];

        // Institution names from financial_accounts (first match for the
        // account holder; fall back to a human-cased kind).
        const accts = acctsRes.data || [];
        const holderAccts = accts.filter((a) => a.member_id === member.id);
        const firstInst = (kind) => {
          const a = holderAccts.find((x) => x.account_kind === kind);
          return a ? a.institution : kindLabel(kind);
        };

        // Reshape value_events into the prototype's expected event shape and
        // assign a per-member event-index ("cycle N for this member through
        // this step") so the velocity chart and ledger pills have something
        // meaningful to show.
        const eventsAsc = eventsRes.data || [];
        const cycleByMember = {};
        const eventsShaped = eventsAsc.map((e) => {
          const owner = e.account_owner_id;
          cycleByMember[owner] = (cycleByMember[owner] || 0) + 1;
          return {
            id: e.id,
            date: e.event_date,
            dateLabel: fmtDate(e.event_date),
            time: fmtTime(e.event_date),
            member: owner,
            amount: Number(e.amount_moved || 0),
            value: Number(e.value_generated || 0),
            kareemShare: Number(e.kareem_share || 0),
            investorShare: Number(e.investor_share || 0),
            hurdleAfter: Number(e.hurdle_balance_after || 0),
            rateSnapshot: Number(e.value_rate_snapshot || 0),
            // D23 (T-022): per-txn fee snapshotted at post; null = no fee.
            feeAmount: e.fee_amount == null ? null : Number(e.fee_amount),
            cycle: cycleByMember[owner],
          };
        });
        // Newest-first for ledger render (matches prototype).
        const eventsDesc = eventsShaped.slice().reverse();

        // Step + loop memberships.
        const loops = loopsRes.data || [];
        const allRefs = allRefsRes.data || [];
        const mySubLoopIds = new Set((mySubsRes.data || []).map((s) => s.loop_id));

        const refsByLoop = {};
        allRefs.forEach((r) => {
          (refsByLoop[r.loop_id] = refsByLoop[r.loop_id] || []).push(r);
        });
        const myRefs = (refsRes.data || []);
        const LOOPS_CONTAINING = loops.map((l) => {
          const refsInLoop = (refsByLoop[l.id] || []).slice().sort((a, b) => a.step_order - b.step_order);
          const myRef = myRefs.find((r) => r.loop_id === l.id);
          // Position + label as the loop is drawn: a branch (Step 1B / 1C,
          // the same load on either card) is one position, not two.
          const labelled = labelLoopSteps(refsInLoop.map((r) => ({ ...(r.steps || {}), step_id: r.step_id })));
          const mine = labelled.find((s) => s.step_id === stepId);
          return {
            id: l.id,
            name: l.name,
            theme: THEME_HEX[l.theme_color] || '#4f46e5',
            glyph: themeGlyph(l.id),
            order: mine ? mine.position : (myRef ? myRef.step_order : 1),
            orderLabel: mine ? mine.label : String(myRef ? myRef.step_order : 1),
            totalSteps: labelled.length ? labelled[0].positions : refsInLoop.length,
            subscribed: mySubLoopIds.has(l.id),
          };
        });

        // Step in render shape.
        const STEP = {
          id: step.id,
          sourceInst: firstInst(step.source_kind),
          sourceKind: step.source_kind,
          sourceKindLabel: kindLabel(step.source_kind),
          destInst: firstInst(step.dest_kind),
          destKind: step.dest_kind,
          destKindLabel: kindLabel(step.dest_kind),
          rate: Number(step.value_rate || 0),
          feeRate: step.fee_rate == null ? 0 : Number(step.fee_rate), // D23
          status: step.status,
          activatedAt: step.activated_at,
          authorName: step.created_by_member_id || '—',
          notes: step.notes || '',
        };

        setState({
          status: 'ready',
          data: { STEP, FAMILY, EVENTS: eventsDesc, LOOPS_CONTAINING },
          error: null,
        });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', data: null, error: err });
      }
    })();
    return () => { cancelled = true; };
  }, [stepId, member?.id]);

  return state;
}

// ─── Avatar ───
const Avatar = ({ member, size = 28 }) => (
  <div className="avatar" style={{
    width: size, height: size,
    background: `linear-gradient(135deg, ${member.color}, ${shade(member.color, -22)})`,
    fontSize: size * 0.42,
  }}>{member.initials}</div>
);

// ─── Hero — the step flow ───
const StepHero = ({ STEP, FAMILY, LOOPS_CONTAINING, totalValue, totalCycled, totalMoves }) => {
  return (
    <section className="hero">
      <div className="hero-status-row">
        <span className="status-pill">
          <span className="status-dot"></span>
          {(STEP.status || 'active').toUpperCase()}
        </span>
        <span className="hero-meta">
          Authored by <strong>{STEP.authorName}</strong>
          {STEP.activatedAt ? <> · Activated <strong>{fmtDate(STEP.activatedAt)}</strong></> : null}
          {' '}· Step ID <span className="mono">#{(STEP.id || '').slice(0, 8)}</span>
        </span>
      </div>

      <div className="hero-flow">
        <div className="hero-acct hero-acct-source">
          <div className="hero-acct-logo">
            <span>{initial(STEP.sourceInst)}</span>
          </div>
          <div className="hero-acct-body">
            <div className="hero-acct-kind">SOURCE · <span className="mono">{STEP.sourceKind}</span></div>
            <div className="hero-acct-name">{STEP.sourceInst}</div>
            <div className="hero-acct-sub">{STEP.sourceKindLabel}</div>
          </div>
        </div>

        <div className="hero-conduit">
          <div className="hero-conduit-line">
            <svg className="hero-conduit-svg" viewBox="0 0 200 60" preserveAspectRatio="none">
              <defs>
                <linearGradient id="hg" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor="#a5b4fc"/>
                  <stop offset="50%" stopColor="#6ee7b7"/>
                  <stop offset="100%" stopColor="#34d399"/>
                </linearGradient>
              </defs>
              <path d="M0 30 L200 30" stroke="url(#hg)" strokeWidth="3" strokeLinecap="round"/>
              <circle r="3" fill="#34d399">
                <animateMotion dur="2.4s" repeatCount="indefinite" path="M0 30 L200 30"/>
              </circle>
              <circle r="3" fill="#6ee7b7" opacity="0.7">
                <animateMotion dur="2.4s" repeatCount="indefinite" begin="0.8s" path="M0 30 L200 30"/>
              </circle>
              <circle r="3" fill="#a5b4fc" opacity="0.5">
                <animateMotion dur="2.4s" repeatCount="indefinite" begin="1.6s" path="M0 30 L200 30"/>
              </circle>
            </svg>
          </div>
          <div className="hero-rate-card">
            <div className="hero-rate-eyebrow">value rate</div>
            <div className="hero-rate-val">
              <span className="hero-rate-num">{(STEP.rate * 100).toFixed(1)}</span>
              <span className="hero-rate-pct">%</span>
            </div>
            <div className="hero-rate-sub">per dollar moved</div>
          </div>
        </div>

        <div className="hero-acct hero-acct-dest">
          <div className="hero-acct-body" style={{textAlign:'right'}}>
            <div className="hero-acct-kind">DEST · <span className="mono">{STEP.destKind}</span></div>
            <div className="hero-acct-name">{STEP.destInst}</div>
            <div className="hero-acct-sub">{STEP.destKindLabel}</div>
          </div>
          <div className="hero-acct-logo hero-acct-logo-dest">
            <span>{initial(STEP.destInst)}</span>
          </div>
        </div>
      </div>

      <div className="hero-bottom-strip">
        <HeroKpi label="Moves YTD" value={totalMoves} accent="indigo" />
        <HeroKpi label="Cycled YTD" value={fmt$Big(totalCycled)} accent="indigo" />
        <HeroKpi label="Value generated" value={'+' + fmt$(totalValue)} accent="green" highlight />
        <HeroKpi label="Members" value={FAMILY.length} accent="indigo" />
        <HeroKpi label="Loops using this step" value={LOOPS_CONTAINING.length} accent="indigo" />
      </div>
    </section>
  );
};

const HeroKpi = ({ label, value, accent, highlight }) => (
  <div className={'hk hk-' + accent + (highlight ? ' hk-highlight' : '')}>
    <div className="hk-val">{value}</div>
    <div className="hk-lbl">{label}</div>
  </div>
);

// ─── Velocity sparkline ───
const VelocityCard = ({ events }) => {
  // Group by cycle (1-indexed per member), cumulative value across all members.
  // The "cycle" axis here is "Nth time any member went through this step",
  // so we group events by max-of-per-member-cycle.
  if (!events.length) return null;
  const oldestFirst = events.slice().reverse();
  const cycles = {};
  oldestFirst.forEach((e) => {
    if (!cycles[e.cycle]) cycles[e.cycle] = { cycle: e.cycle, value: 0, moves: 0, firstDate: e.date };
    cycles[e.cycle].value += e.value;
    cycles[e.cycle].moves += 1;
  });
  const points = Object.values(cycles).sort((a, b) => a.cycle - b.cycle);
  let cumValue = 0;
  const cumPoints = points.map(p => {
    cumValue += p.value;
    return { ...p, cum: cumValue };
  });

  const maxCum = cumValue || 1;
  const W = 600, H = 120, PAD = 20;
  const xStep = (W - 2 * PAD) / (cumPoints.length - 1 || 1);
  const yScale = (v) => H - PAD - (v / maxCum) * (H - 2 * PAD - 10);

  const pathD = cumPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${PAD + i * xStep} ${yScale(p.cum)}`).join(' ');
  const areaD = pathD + ` L ${PAD + (cumPoints.length - 1) * xStep} ${H - PAD} L ${PAD} ${H - PAD} Z`;

  return (
    <section className="card velocity-card">
      <header className="card-header">
        <div className="card-title-block">
          <div className="card-eyebrow">Cumulative value</div>
          <h3 className="card-title">Velocity</h3>
        </div>
        <div className="velocity-summary">
          <div className="vs-val">+{fmt$(cumValue)}</div>
          <div className="vs-sub">across {cumPoints.length} {cumPoints.length === 1 ? 'cycle' : 'cycles'}</div>
        </div>
      </header>
      <div className="velocity-chart">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H}>
          <defs>
            <linearGradient id="vg" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#059669" stopOpacity="0.25"/>
              <stop offset="100%" stopColor="#059669" stopOpacity="0"/>
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75].map(r => (
            <line key={r}
              x1={PAD} x2={W - PAD}
              y1={H - PAD - r * (H - 2 * PAD - 10)} y2={H - PAD - r * (H - 2 * PAD - 10)}
              stroke="#e5e7ef" strokeWidth="1" strokeDasharray="2 4"
            />
          ))}
          <path d={areaD} fill="url(#vg)"/>
          <path d={pathD} fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          {cumPoints.map((p, i) => (
            <g key={i}>
              <circle cx={PAD + i * xStep} cy={yScale(p.cum)} r="4" fill="#fff" stroke="#059669" strokeWidth="2"/>
              <text x={PAD + i * xStep} y={yScale(p.cum) - 12} fontSize="10" fontWeight="700" textAnchor="middle" fill="#059669">+${p.cum}</text>
            </g>
          ))}
        </svg>
      </div>
      <div className="velocity-axis">
        {cumPoints.map((p, i) => (
          <div key={i} className="va-label">
            <div className="va-cycle">Cycle {p.cycle}</div>
            <div className="va-date">{fmtDate(p.firstDate)}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─── Family activity ───
const FamilyActivity = ({ FAMILY, events, focused, setFocused }) => {
  const perMember = FAMILY.map(m => {
    const memberEvents = events.filter(e => e.member === m.id);
    return {
      ...m,
      moves: memberEvents.length,
      cycled: memberEvents.reduce((s, e) => s + e.amount, 0),
      value: memberEvents.reduce((s, e) => s + e.value, 0),
      lastDate: memberEvents[0]?.date,
    };
  });
  const maxValue = Math.max(1, ...perMember.map(m => m.value));

  return (
    <section className="card family-activity-card">
      <header className="card-header">
        <div className="card-title-block">
          <div className="card-eyebrow">Per-member breakdown</div>
          <h3 className="card-title">Family activity through this step</h3>
        </div>
      </header>
      <div className="fa-rows">
        {perMember.map(m => {
          const pct = (m.value / maxValue) * 100;
          const isFocused = focused === m.id;
          const isDim = focused && focused !== m.id;
          return (
            <button
              key={m.id}
              className={'fa-row' + (isFocused ? ' is-focused' : '') + (isDim ? ' is-dim' : '')}
              onClick={() => setFocused(focused === m.id ? null : m.id)}
              style={{ '--member-color': m.color }}
            >
              <Avatar member={m} size={36} />
              <div className="fa-id">
                <div className="fa-name">{m.name}</div>
                <div className="fa-role">{m.role === 'holder' ? 'Account holder' : 'Sub-account'}</div>
              </div>
              <div className="fa-bar-wrap">
                <div className="fa-bar-track">
                  <div className="fa-bar-fill" style={{ width: pct + '%' }}></div>
                </div>
                <div className="fa-stats">
                  <span className="fa-stat"><strong>{m.moves}</strong> moves</span>
                  <span className="fa-stat-dot">·</span>
                  <span className="fa-stat"><strong>{fmt$Big(m.cycled)}</strong> cycled</span>
                  <span className="fa-stat-dot">·</span>
                  <span className="fa-stat fa-stat-value">+{fmt$(m.value)} value</span>
                </div>
              </div>
              <div className="fa-last">
                <div className="fa-last-label">Last move</div>
                <div className="fa-last-date">{m.lastDate ? fmtDate(m.lastDate) : '—'}</div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

// ─── Movement ledger ───
const MovementLedger = ({ STEP, FAMILY, events, focusedMember }) => {
  const filtered = focusedMember ? events.filter(e => e.member === focusedMember) : events;
  const findMember = (id) => FAMILY.find(m => m.id === id) || { name: id, initials: '?', color: '#94a3b8' };

  return (
    <section className="card ledger-card">
      <header className="card-header">
        <div className="card-title-block">
          <div className="card-eyebrow">Append-only ledger</div>
          <h3 className="card-title">
            Movement history
            {focusedMember && <span className="card-title-focus"> · {findMember(focusedMember).name}</span>}
            <span className="card-title-count"> ({filtered.length} {filtered.length === 1 ? 'event' : 'events'})</span>
          </h3>
        </div>
      </header>

      {filtered.length === 0 ? (
        <div style={{ padding: '24px 20px', color: 'var(--text-3, #94a3b8)', textAlign: 'center', fontSize: 14 }}>
          No value events through this step yet. Once movements get matched (manual entry, or once the email agent lands), they'll appear here.
        </div>
      ) : (
        <div className="ledger-table">
          <div className="lt-head">
            <div>Date</div>
            <div>Member</div>
            <div className="text-right">Amount moved</div>
            <div className="text-right">Rate</div>
            <div className="text-right">Value generated</div>
            <div className="text-right">Hurdle remaining</div>
          </div>
          {filtered.map((e, i) => {
            const m = findMember(e.member);
            const dateLabel = i > 0 && filtered[i-1].dateLabel === e.dateLabel ? '' : e.dateLabel;
            return (
              <div key={e.id} className="lt-row">
                <div className="lt-date">
                  {dateLabel && <div className="lt-date-main">{dateLabel}</div>}
                  <div className="lt-time mono">{e.time}</div>
                </div>
                <div className="lt-member">
                  <Avatar member={m} size={24} />
                  <span>{m.name}</span>
                  <span className="lt-cycle-pill">C{e.cycle}</span>
                </div>
                <div className="text-right mono">{fmt$(e.amount)}</div>
                <div className="text-right">
                  <span className="lt-rate-pill">{fmtPct(e.rateSnapshot)}</span>
                </div>
                <div className="text-right mono lt-value">{e.value === 0 ? '$0' : '+' + fmt$(e.value)}</div>
                <div className="text-right mono lt-hurdle">{fmt$(e.hurdleAfter)}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="ledger-foot">
        <span className="lf-pill kareem">All value → Kareem until hurdle clears</span>
        <span className="lf-text">Value math: <span className="mono">amount × rate</span>, at the rate each event was posted with (the pill on its row — the step's current rate is {fmtPct(STEP.rate)}).{Number(STEP.feeRate || 0) > 0 && <> A {fmtPct(STEP.feeRate / (1 - STEP.feeRate), 2)} fee on the load is added on top of each charge; only value minus fee counts toward the hurdle, and settlement pays the fee back to the investor.</>} Hurdle balance shown is the running ledger after each event.</span>
      </div>
    </section>
  );
};

// ─── Loop memberships ───
const LoopMemberships = ({ LOOPS_CONTAINING }) => (
  <section className="card loops-card">
    <header className="card-header">
      <div className="card-title-block">
        <div className="card-eyebrow">Loop memberships</div>
        <h3 className="card-title">
          This step is part of {LOOPS_CONTAINING.length} loop{LOOPS_CONTAINING.length === 1 ? '' : 's'}
        </h3>
      </div>
    </header>
    {LOOPS_CONTAINING.length === 0 ? (
      <div style={{ padding: '20px', color: 'var(--text-3, #94a3b8)', fontSize: 14 }}>
        Not part of any loop yet. Kareem can add it from the Catalog admin (Investment → Catalog).
      </div>
    ) : (
      <div className="lm-rows">
        {LOOPS_CONTAINING.map(loop => (
          <div key={loop.id} className="lm-row" style={{ '--theme': loop.theme }}>
            <div className="lm-glyph">{loop.glyph}</div>
            <div className="lm-body">
              <div className="lm-name">{loop.name}</div>
              <div className="lm-meta">Step {loop.orderLabel || loop.order} of {loop.totalSteps} · {loop.subscribed ? 'Subscribed' : 'Available'}</div>
            </div>
            <div className="lm-order">
              <div className="lm-order-dots">
                {Array.from({ length: loop.totalSteps }).map((_, i) => (
                  <div key={i} className={'lm-order-dot' + (i + 1 === loop.order ? ' is-current' : '')}></div>
                ))}
              </div>
              <div className="lm-order-label">Step <strong>{loop.orderLabel || loop.order}</strong> / {loop.totalSteps}</div>
            </div>
            <a href="#home" className="btn-primary-sm" style={{textDecoration:'none'}}>Open loop →</a>
          </div>
        ))}
      </div>
    )}
  </section>
);

// ─── Step details meta card ───
const StepMeta = ({ STEP }) => (
  <section className="card meta-card">
    <header className="card-header">
      <div className="card-title-block">
        <div className="card-eyebrow">Step details</div>
        <h3 className="card-title">About this step</h3>
      </div>
    </header>
    {STEP.notes ? <p className="meta-notes">{STEP.notes}</p> : null}
    <div className="meta-grid">
      <div className="meta-item">
        <div className="meta-label">Source kind</div>
        <div className="meta-value mono">{STEP.sourceKind}</div>
      </div>
      <div className="meta-item">
        <div className="meta-label">Dest kind</div>
        <div className="meta-value mono">{STEP.destKind}</div>
      </div>
      <div className="meta-item">
        <div className="meta-label">Current rate</div>
        <div className="meta-value">{fmtPct(STEP.rate)}</div>
      </div>
      <div className="meta-item">
        <div className="meta-label">Status</div>
        <div className="meta-value">
          <span className="status-pill status-pill-sm">
            <span className="status-dot"></span>
            {(STEP.status || 'active').replace(/\b\w/g, (c) => c.toUpperCase())}
          </span>
        </div>
      </div>
      {STEP.activatedAt ? (
        <div className="meta-item">
          <div className="meta-label">Activated</div>
          <div className="meta-value">{fmtDate(STEP.activatedAt)}</div>
        </div>
      ) : null}
      <div className="meta-item">
        <div className="meta-label">Author</div>
        <div className="meta-value">{STEP.authorName}</div>
      </div>
    </div>
  </section>
);

// ─── App root ───
function StepDetail({ member, params }) {
  const stepId = params?.stepId || null;
  const { status, data, error } = useStepDetailData(stepId, member);
  const [focusedMember, setFocusedMember] = useState(null);

  if (status === 'loading') {
    return <main className="page" style={{ padding: 32, color: 'var(--text-3, #94a3b8)' }}>Loading step…</main>;
  }
  if (status === 'no-step') {
    return (
      <main className="page" style={{ padding: 32 }}>
        <nav className="crumbs">
          <a href="#home" className="crumb">Investment</a>
          <span className="crumb-sep">›</span>
          <span className="crumb-current">Step detail</span>
        </nav>
        <div style={{ background: 'var(--accent-bg)', border: '1px solid var(--accent-soft)', padding: 16, borderRadius: 'var(--radius-lg)', maxWidth: 640, marginTop: 16, color: 'var(--accent-2)' }}>
          <strong>No step selected.</strong>
          <p style={{ margin: '8px 0 0' }}>Open a step from the loop on the Investment Home page.</p>
        </div>
      </main>
    );
  }
  if (status === 'not-found') {
    return (
      <main className="page" style={{ padding: 32 }}>
        <nav className="crumbs">
          <a href="#home" className="crumb">Investment</a>
          <span className="crumb-sep">›</span>
          <span className="crumb-current">Step detail</span>
        </nav>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#9f1239', padding: 16, borderRadius: 12, maxWidth: 640, marginTop: 16 }}>
          <strong>Step not found.</strong>
          <p style={{ margin: '8px 0 0' }}>It may have been removed from the catalog.</p>
        </div>
      </main>
    );
  }
  if (status === 'error') {
    return (
      <main className="page" style={{ padding: 32 }}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#9f1239', padding: 16, borderRadius: 12, maxWidth: 640 }}>
          <strong>Couldn't load step.</strong>
          <p style={{ fontFamily: 'monospace', fontSize: 12, margin: '8px 0 0' }}>{String(error?.message || error)}</p>
        </div>
      </main>
    );
  }

  const { STEP, FAMILY, EVENTS, LOOPS_CONTAINING } = data;
  const totalValue  = EVENTS.reduce((s, e) => s + e.value, 0);
  const totalCycled = EVENTS.reduce((s, e) => s + e.amount, 0);
  const totalMoves  = EVENTS.length;
  const primaryLoop = LOOPS_CONTAINING[0];

  return (
    <div className="app">
      <main className="page">
        <nav className="crumbs">
          <a href="#home" className="crumb">Investment</a>
          {primaryLoop && (
            <>
              <span className="crumb-sep">›</span>
              <a href="#home" className="crumb">
                <span className="crumb-glyph" style={{color: primaryLoop.theme}}>{primaryLoop.glyph}</span>
                {primaryLoop.name}
              </a>
            </>
          )}
          <span className="crumb-sep">›</span>
          <span className="crumb-current">{STEP.sourceInst} → {STEP.destInst}</span>
        </nav>

        <div className="page-title-row">
          <div>
            <div className="page-eyebrow">Step detail</div>
            <h1 className="page-title">
              <span className="pt-acct">{STEP.sourceInst}</span>
              <span className="pt-arrow">→</span>
              <span className="pt-acct">{STEP.destInst}</span>
            </h1>
            <p className="page-sub">
              One of Kareem's catalog steps. Value flows when any subscribed member moves money from <span className="mono">{STEP.sourceKind}</span> to <span className="mono">{STEP.destKind}</span>.
            </p>
          </div>
          <div className="page-actions">
            <a className="btn-ghost" href="#home">← Back</a>
          </div>
        </div>

        <StepHero STEP={STEP} FAMILY={FAMILY} LOOPS_CONTAINING={LOOPS_CONTAINING} totalValue={totalValue} totalCycled={totalCycled} totalMoves={totalMoves} />

        <div className="two-col">
          {EVENTS.length > 0 && <VelocityCard events={EVENTS} />}
          <StepMeta STEP={STEP} />
        </div>

        <FamilyActivity FAMILY={FAMILY} events={EVENTS} focused={focusedMember} setFocused={setFocusedMember} />

        <MovementLedger STEP={STEP} FAMILY={FAMILY} events={EVENTS} focusedMember={focusedMember} />

        <LoopMemberships LOOPS_CONTAINING={LOOPS_CONTAINING} />

        <footer className="page-foot">
          Value math: <span className="mono">amount × the rate posted with each event</span> (current step rate {fmtPct(STEP.rate)}) · append-only · hurdle balance after each event tracks the running ledger with Kareem
        </footer>
      </main>
    </div>
  );
}

export default StepDetail;
